import React, { useState, useEffect } from 'react';
import CountryFlag from '../../../shared/components/CountryFlag';
import {
    Table,
    Button,
    Space,
    Tag,
    Modal,
    Form,
    Input,
    Select,
    message,
    Card,
    Drawer,
    Popconfirm,
    Tooltip,
    InputNumber,
    Descriptions,
    Badge,
    Tabs,
    Progress,
    Typography,
    Switch,
    Checkbox,
    Collapse
} from 'antd';
import {
    PlusOutlined,
    DeleteOutlined,
    EditOutlined,
    CloudOutlined,
    SyncOutlined,
    GlobalOutlined,
    UnorderedListOutlined
} from '@ant-design/icons';
import evmAdminService from '../../../shared/services/evmAdminService';
import Ipv6PrefixDrawer from './Ipv6PrefixDrawer';
import { RdnsPoolModal, RdnsRequestsDrawer, RDNS_MODE_COLORS } from './RdnsAdmin';

const { Option } = Select;
const { TextArea } = Input;
const { Text } = Typography;

const ACCESS_MODES = [
    { value: 'bridged', label: '桥接' },
    { value: 'routed', label: '路由' },
];
const ACCESS_MODE_TIP = '桥接：网卡挂上联网桥（每个虚拟 MAC 一块网卡）；路由：宿主机把 IP 路由给 VM（子网/故障转移 IP/IPv6）';

const IpPoolManagement = () => {
    const [pools, setPools] = useState([]);
    const [rdnsPool, setRdnsPool] = useState(null);
    const [rdnsOpen, setRdnsOpen] = useState(false);
    const [areas, setAreas] = useState([]);
    const [zones, setZones] = useState([]);
    const [nodes, setNodes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [createDrawerVisible, setCreateDrawerVisible] = useState(false);
    const [addressModalVisible, setAddressModalVisible] = useState(false);
    const [addAddressModalVisible, setAddAddressModalVisible] = useState(false);
    const [currentPool, setCurrentPool] = useState(null);
    const [poolAddresses, setPoolAddresses] = useState([]);
    const [addressLoading, setAddressLoading] = useState(false);
    const [createType, setCreateType] = useState('v4');
    const [preallocModalVisible, setPreallocModalVisible] = useState(false);
    const [v6Pool, setV6Pool] = useState(null);

    const [createForm] = Form.useForm();
    const [addressForm] = Form.useForm();
    const [preallocForm] = Form.useForm();
    const v6Alloc = Form.useWatch('alloc', createForm);

    useEffect(() => {
        loadPools();
        loadAreas();
        loadNodes();
    }, []);

    const loadPools = async () => {
        setLoading(true);
        try {
            const response = await evmAdminService.getIpPoolList();
            if (response.status === 'success') {
                // Handle paginated response: data.data contains the actual array
                const poolsData = Array.isArray(response.data)
                    ? response.data
                    : (response.data?.data || response.data?.pools || []);
                setPools(poolsData);
            } else {
                message.error(response.msg || 'Failed to load IP pools');
            }
        } catch (error) {
            message.error('Failed to load IP pools');
        } finally {
            setLoading(false);
        }
    };

    const loadAreas = async () => {
        try {
            const response = await evmAdminService.getAreas();
            if (response.status === 'success') {
                // Handle paginated response: data.data contains the actual array
                const areasData = Array.isArray(response.data)
                    ? response.data
                    : (response.data?.data || response.data?.areas || []);
                setAreas(areasData);
            }
        } catch (error) {
            console.error('Failed to load areas', error);
        }
    };

    const loadNodes = async () => {
        try {
            const response = await evmAdminService.getNodeList();
            if (response.status === 'success') {
                // Handle paginated response
                const nodesData = Array.isArray(response.data)
                    ? response.data
                    : (response.data?.data || response.data?.nodes || []);
                setNodes(nodesData);
            }
        } catch (error) {
            console.error('Failed to load nodes', error);
        }
    };

    const loadZonesByArea = async (areaId) => {
        try {
            const response = await evmAdminService.getZonesByArea(areaId);
            if (response.status === 'success') {
                // Handle paginated response
                const zonesData = Array.isArray(response.data)
                    ? response.data
                    : (response.data?.data || response.data?.zones || []);
                setZones(zonesData);
            }
        } catch (error) {
            console.error('Failed to load zones', error);
        }
    };

    const loadPoolAddresses = async (poolId) => {
        setAddressLoading(true);
        try {
            const response = await evmAdminService.getIpPoolAddresses(poolId);
            if (response.status === 'success') {
                // Handle paginated response
                const addressData = Array.isArray(response.data)
                    ? response.data
                    : (response.data?.data || response.data?.addresses || []);
                setPoolAddresses(addressData);
            } else {
                message.error(response.msg || 'Failed to load addresses');
            }
        } catch (error) {
            message.error('Failed to load addresses');
        } finally {
            setAddressLoading(false);
        }
    };

    const handleDelete = async (pool) => {
        try {
            const response = await evmAdminService.deleteIpPool(pool.id);
            if (response.status === 'success') {
                message.success('IP Pool deleted');
                loadPools();
            } else {
                message.error(response.msg || 'Delete failed');
            }
        } catch (error) {
            message.error('Delete failed');
        }
    };

    const showCreateDrawer = (type) => {
        setCreateType(type);
        createForm.resetFields();
        setZones([]);
        setCreateDrawerVisible(true);
    };

    const showAddressModal = async (pool) => {
        setCurrentPool(pool);
        await loadPoolAddresses(pool.id);
        setAddressModalVisible(true);
    };

    const showAddAddressModal = (pool) => {
        setCurrentPool(pool);
        addressForm.resetFields();
        setAddAddressModalVisible(true);
    };

    const showPreallocModal = (pool) => {
        setCurrentPool(pool);
        preallocForm.resetFields();
        preallocForm.setFieldsValue({ count: 1 });
        setPreallocModalVisible(true);
    };

    const handlePreallocate = async (values) => {
        try {
            const response = await evmAdminService.preallocateIpV6({
                pool_id: currentPool.id,
                count: values.count
            });
            if (response.status === 'success') {
                message.success(`Preallocated ${response.data?.count || 0} IPv6 addresses`);
                setPreallocModalVisible(false);
                loadPools();
            } else {
                message.error(response.msg || 'Failed to preallocate IPv6 addresses');
            }
        } catch (error) {
            message.error('Failed to preallocate IPv6 addresses');
        }
    };

    const handleFailoverChange = async (pool, failover) => {
        try {
            const response = await evmAdminService.updateIpPool(pool.id, { failover: failover ? 1 : 0 });
            if (response.status === 'success') {
                loadPools();
            } else {
                message.error(response.msg || 'Update failed');
            }
        } catch (error) {
            message.error('Update failed');
        }
    };

    const handleNodesChange = async (pool, nodeIds) => {
        const response = await evmAdminService.updateIpPool(pool.id, { node_ids: nodeIds.length ? nodeIds : '' });
        if (response.status === 'success') {
            setPools(list => list.map(p => p.id === pool.id ? { ...p, node_ids: response.data?.node_ids ?? nodeIds } : p));
        } else {
            message.error(response.msg || 'Update failed');
        }
    };

    const handleAccessModeChange = async (pool, mode) => {
        try {
            const response = await evmAdminService.updateIpPool(pool.id, { access_mode: mode });
            if (response.status === 'success') {
                message.success('已更新，已绑定的实例在下次网络变更时生效');
                loadPools();
            } else {
                message.error(response.msg || 'Update failed');
            }
        } catch (error) {
            message.error('Update failed');
        }
    };

    const handleCreate = async (values) => {
        try {
            let response;
            if (createType === 'v4') {
                // Backend createV4 requires `mask` (not `netmask`); v4 pools use
                // gateway+mask, not cidr.
                const v4Data = { ...values, mask: values.mask || values.netmask };
                delete v4Data.netmask;
                response = await evmAdminService.createIpPoolV4(v4Data);
            } else {
                // cidr "2001:db8::/48" -> prefix + prefix_len
                const [pfx, plen] = String(values.cidr || '').trim().split('/');
                const v6Data = {
                    ...values,
                    prefix: pfx,
                    prefix_len: plen ? Number(plen) : values.prefix_len,
                };
                delete v6Data.cidr;
                if (values.alloc) {
                    v6Data.alloc = 1;
                    v6Data.access_mode = 'routed';
                    v6Data.ndppd_enabled = 0;
                    if (Array.isArray(values.alloc_sizes)) v6Data.alloc_sizes = values.alloc_sizes.join(',');
                } else {
                    delete v6Data.alloc;
                    delete v6Data.alloc_sizes;
                    delete v6Data.default_len;
                    delete v6Data.cooldown_days;
                }
                response = await evmAdminService.createIpPoolV6(v6Data);
            }
            if (response.status === 'success') {
                message.success('IP Pool created');
                setCreateDrawerVisible(false);
                loadPools();
            } else {
                message.error(response.msg || 'Failed to create IP pool');
            }
        } catch (error) {
            message.error('Failed to create IP pool');
        }
    };

    const handleAddAddresses = async (values) => {
        try {
            let response;
            if (values.mode === 'list') {
                const addresses = values.addresses.split('\n').map(a => a.trim()).filter(a => a);
                response = await evmAdminService.addIpAddresses({
                    pool_id: currentPool.id,
                    addresses: addresses
                });
            } else {
                response = await evmAdminService.addIpAddressRange({
                    pool_id: currentPool.id,
                    start_ip: values.start,
                    end_ip: values.end
                });
            }
            if (response.status === 'success') {
                const added = response.data?.count ?? response.data?.added_count ?? response.data?.added ?? 0;
                const failed = response.data?.failed || [];
                if (failed.length) {
                    Modal.warning({
                        title: `已添加 ${added} 个，${failed.length} 个失败`,
                        content: failed.slice(0, 30).map(f => `${f.address}：${f.reason}`).join('\n'),
                        styles: { body: { whiteSpace: 'pre-line' } },
                    });
                } else {
                    message.success(`已添加 ${added} 个地址`);
                }
                setAddAddressModalVisible(false);
                loadPools();
            } else {
                message.error(response.msg || 'Failed to add addresses');
            }
        } catch (error) {
            message.error('Failed to add addresses');
        }
    };

    const getTypeTag = (type) => {
        return type === 'v6' ? (
            <Tag color="purple">IPv6</Tag>
        ) : (
            <Tag color="blue">IPv4</Tag>
        );
    };

    const getStatusTag = (status) => {
        const statusMap = {
            'active': { color: 'green', text: 'Active' },
            'inactive': { color: 'red', text: 'Inactive' },
            'full': { color: 'orange', text: 'Full' }
        };
        const config = statusMap[status] || { color: 'default', text: status };
        return <Tag color={config.color}>{config.text}</Tag>;
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 60
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            width: 150
        },
        {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
            width: 80,
            render: (type, r) => (
                <Space size={4}>
                    {getTypeTag(type)}
                    {type === 'v6' && Number(r.v6_alloc ?? r.alloc ?? 0) === 1 && <Tag color="geekblue">前缀</Tag>}
                </Space>
            )
        },
        {
            title: 'CIDR',
            dataIndex: 'cidr',
            key: 'cidr',
            width: 160
        },
        {
            title: 'Gateway',
            dataIndex: 'gateway',
            key: 'gateway',
            width: 140
        },
        {
            title: <Tooltip title={ACCESS_MODE_TIP}>模式</Tooltip>,
            dataIndex: 'access_mode',
            key: 'access_mode',
            width: 100,
            render: (mode, record) => (
                <Space size={4}>
                    <Select
                        size="small"
                        value={mode || 'bridged'}
                        options={ACCESS_MODES}
                        onChange={(v) => handleAccessModeChange(record, v)}
                        style={{ width: 80 }}
                    />
                    {mode === 'routed' && record.failover !== undefined && (
                        <Tooltip title="服务商故障转移 IP：实例迁移时可随迁（需在服务商处改路由）">
                            <Checkbox checked={!!Number(record.failover)} onChange={(e) => handleFailoverChange(record, e.target.checked)}>漂移</Checkbox>
                        </Tooltip>
                    )}
                </Space>
            )
        },
        {
            title: <Tooltip title="可绑定多个节点；分配 IP 只用已绑定节点的池">节点</Tooltip>,
            dataIndex: 'node_ids',
            key: 'node_ids',
            width: 240,
            render: (ids, record) => (
                <Select
                    mode="multiple"
                    size="small"
                    style={{ width: '100%' }}
                    placeholder="未绑定"
                    value={ids || []}
                    options={nodes.map(n => ({ value: n.id, label: n.name }))}
                    onChange={(v) => handleNodesChange(record, v)}
                    maxTagCount="responsive"
                />
            )
        },
        {
            title: <Tooltip title="客户设置反向解析(PTR)的方式">rDNS</Tooltip>,
            dataIndex: 'rdns_mode',
            key: 'rdns_mode',
            width: 90,
            render: (mode, record) => record.netType === 'public' && mode !== undefined && (
                <Tag color={RDNS_MODE_COLORS[mode || 'none']} style={{ cursor: 'pointer' }} onClick={() => setRdnsPool(record)}>
                    {{ zone: '我们的DNS', manual: '上游面板', none: '不提供' }[mode || 'none']}
                </Tag>
            )
        },
        {
            title: 'Usage',
            key: 'usage',
            width: 180,
            render: (_, record) => {
                if (record.type === 'v6' && Number(record.v6_alloc ?? record.alloc ?? 0) === 1) {
                    return <Button size="small" type="link" onClick={() => setV6Pool(record)}>前缀分配</Button>;
                }
                const total = record.total_count || 0;
                const used = record.used_count || 0;
                const percent = total > 0 ? Math.round((used / total) * 100) : 0;
                return (
                    <div>
                        <Progress percent={percent} size="small" />
                        <Text type="secondary">{used}/{total}</Text>
                    </div>
                );
            }
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 80,
            render: (status) => getStatusTag(status)
        },
        {
            title: 'Actions',
            key: 'action',
            fixed: 'right',
            width: 200,
            render: (_, record) => (
                <Space size="small">
                    <Tooltip title="View Addresses">
                        <Button
                            size="small"
                            icon={<UnorderedListOutlined />}
                            onClick={() => showAddressModal(record)}
                        />
                    </Tooltip>
                    <Tooltip title="Add Addresses">
                        <Button
                            size="small"
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => showAddAddressModal(record)}
                        />
                    </Tooltip>
                    {record.type === 'v6' && Number(record.v6_alloc ?? record.alloc ?? 0) !== 1 && (
                        <Tooltip title="Preallocate IPv6">
                            <Button
                                size="small"
                                icon={<GlobalOutlined />}
                                onClick={() => showPreallocModal(record)}
                            />
                        </Tooltip>
                    )}
                    <Popconfirm
                        title="Delete this IP Pool?"
                        description="All addresses will be removed."
                        onConfirm={() => handleDelete(record)}
                    >
                        <Tooltip title="Delete">
                            <Button size="small" danger icon={<DeleteOutlined />} />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    const addressColumns = [
        { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
        { title: 'Address', dataIndex: 'address', key: 'address', width: 160 },
        {
            title: 'MAC',
            dataIndex: 'mac',
            key: 'mac',
            width: 220,
            render: (mac, r) => {
                const used = r.vid > 0 || r.eip_id > 0;
                const text = mac ? <Text code>{mac}</Text> : <Text type="secondary">随机</Text>;
                if (used) return text;
                return (
                    <Text
                        editable={{
                            text: mac || '',
                            tooltip: '修改 MAC（留空 = 随机）',
                            onChange: (v) => handleMacChange(r, v),
                        }}
                    >
                        {text}
                    </Text>
                );
            }
        },
        {
            title: '状态',
            key: 'used',
            width: 120,
            render: (_, r) => r.eip_id > 0
                ? <Tag color="purple">EIP #{r.eip_id}</Tag>
                : r.vid > 0
                    ? <Tag color="blue">VM #{r.vid}</Tag>
                    : r.status === 'enable' ? <Tag color="green">空闲</Tag> : <Tag>停用</Tag>
        }
    ];

    const handleMacChange = async (r, v) => {
        const mac = (v || '').trim();
        if (mac === (r.mac || '')) return;
        const res = await evmAdminService.updateIpAddressMac(r.id, mac);
        if (res.status === 'success') {
            setPoolAddresses(list => list.map(a => a.id === r.id ? { ...a, mac: res.data?.mac ?? mac } : a));
            message.success('已保存');
        } else {
            message.error(res.msg || '保存失败');
        }
    };

    return (
        <div>
            <Card
                title={<span><GlobalOutlined /> IP池管理</span>}
                extra={
                    <Space>
                        <Button type="primary" icon={<PlusOutlined />} onClick={() => showCreateDrawer('v4')}>
                            Create IPv4 Pool
                        </Button>
                        <Button icon={<PlusOutlined />} onClick={() => showCreateDrawer('v6')}>
                            Create IPv6 Pool
                        </Button>
                        <Button onClick={() => setRdnsOpen(true)}>rDNS 请求</Button>
                        <Button icon={<SyncOutlined />} onClick={loadPools}>
                            Refresh
                        </Button>
                    </Space>
                }
            >
                <Table
                    columns={columns}
                    dataSource={pools}
                    loading={loading}
                    rowKey="id"
                    scroll={{ x: 1200 }}
                    pagination={{ pageSize: 20 }}
                />
            </Card>

            {/* Create Pool Drawer */}
            <Drawer
                title={`Create ${createType === 'v6' ? 'IPv6' : 'IPv4'} Pool`}
                width={500}
                open={createDrawerVisible}
                onClose={() => setCreateDrawerVisible(false)}
                extra={
                    <Space>
                        <Button onClick={() => setCreateDrawerVisible(false)}>Cancel</Button>
                        <Button type="primary" onClick={() => createForm.submit()}>Create</Button>
                    </Space>
                }
            >
                <Form form={createForm} layout="vertical" onFinish={handleCreate}>
                    <Form.Item
                        name="name"
                        label="Pool Name"
                        rules={[{ required: true, message: 'Please enter pool name' }]}
                    >
                        <Input placeholder="e.g., us-public-v4" />
                    </Form.Item>

                    <Form.Item
                        name="cidr"
                        label={createType === 'v6' ? 'IPv6 Prefix' : 'CIDR'}
                        rules={[{ required: true, message: createType === 'v6' ? 'Please enter IPv6 prefix' : 'Please enter CIDR' }]}
                        tooltip={createType === 'v6' ? 'e.g., 2001:db8::/48' : 'e.g., 192.168.1.0/24'}
                    >
                        <Input placeholder={createType === 'v6' ? '2001:db8::/48' : '192.168.1.0/24'} />
                    </Form.Item>

                    {createType === 'v6' && (
                        <Form.Item name="alloc" label="前缀分配" valuePropName="checked" tooltip="超网按块分给实例（默认每台 /64），路由模式">
                            <Switch />
                        </Form.Item>
                    )}

                    {createType === 'v6' && v6Alloc && (
                        <Collapse
                            ghost
                            size="small"
                            style={{ marginBottom: 12 }}
                            items={[{
                                key: 'adv',
                                label: '高级',
                                children: (
                                    <>
                                        <Form.Item name="alloc_sizes" label="可分配大小" initialValue={[64, 56, 48]}>
                                            <Select mode="tags" tokenSeparators={[',', ' ']} options={[48, 52, 56, 60, 64].map(v => ({ value: v, label: `/${v}` }))} />
                                        </Form.Item>
                                        <Form.Item name="default_len" label="每实例默认" initialValue={64}>
                                            <InputNumber min={32} max={128} addonBefore="/" style={{ width: '100%' }} />
                                        </Form.Item>
                                        <Form.Item name="cooldown_days" label="释放冷却(天)" initialValue={30}>
                                            <InputNumber min={0} max={365} style={{ width: '100%' }} />
                                        </Form.Item>
                                    </>
                                ),
                            }]}
                        />
                    )}

                    {!(createType === 'v6' && v6Alloc) && (
                    <Form.Item
                        name="gateway"
                        label="Gateway"
                        rules={[{ required: true, message: 'Please enter gateway' }]}
                    >
                        <Input placeholder={createType === 'v6' ? '2001:db8::1' : '192.168.1.1'} />
                    </Form.Item>
                    )}

                    {!(createType === 'v6' && v6Alloc) && (
                    <Form.Item name="access_mode" label="模式" initialValue="bridged" tooltip={ACCESS_MODE_TIP}>
                        <Select options={ACCESS_MODES} />
                    </Form.Item>
                    )}

                    {createType === 'v4' && (
                        <Form.Item name="netmask" label="Netmask">
                            <Input placeholder="e.g., 255.255.255.0" />
                        </Form.Item>
                    )}

                    {createType === 'v6' && !v6Alloc && (
                        <>
                            <Form.Item name="prefix_len" label="Prefix Length" initialValue={64}>
                                <InputNumber min={1} max={128} style={{ width: '100%' }} />
                            </Form.Item>
                            <Form.Item
                                name="ndppd_enabled"
                                label="NDPPD Proxy"
                                valuePropName="checked"
                                initialValue={true}
                                tooltip="Enable NDP Proxy Daemon for IPv6 neighbor discovery"
                            >
                                <Switch checkedChildren="Enabled" unCheckedChildren="Disabled" />
                            </Form.Item>
                            <Form.Item
                                name="physical_nic"
                                label="Physical NIC"
                                tooltip="Physical network interface for NDPPD (e.g., eth0, ens3)"
                            >
                                <Input placeholder="e.g., eth0" />
                            </Form.Item>
                        </>
                    )}

                    <Form.Item name="area_id" label="Area">
                        <Select
                            placeholder="Select area"
                            onChange={(value) => loadZonesByArea(value)}
                            allowClear
                        >
                            {areas.map(area => (
                                <Option key={area.id} value={area.id}><CountryFlag code={area.country} />{area.name}</Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item name="zone_id" label="Zone">
                        <Select placeholder="Select zone" allowClear>
                            {zones.map(zone => (
                                <Option key={zone.id} value={zone.id}>{zone.name}</Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item name="node_ids" label="节点">
                        <Select mode="multiple" placeholder="选择节点" allowClear>
                            {nodes.map(node => (
                                <Option key={node.id} value={node.id}>{node.name} ({node.ipaddress})</Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item name="vlan_id" label="VLAN ID">
                        <InputNumber min={1} max={4094} style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item name="description" label="Description">
                        <TextArea rows={2} />
                    </Form.Item>
                </Form>
            </Drawer>

            {/* View Addresses Modal */}
            <Modal
                title={`Addresses in Pool: ${currentPool?.name || ''}`}
                open={addressModalVisible}
                onCancel={() => setAddressModalVisible(false)}
                footer={null}
                width={800}
            >
                <Table
                    columns={addressColumns}
                    dataSource={poolAddresses}
                    loading={addressLoading}
                    rowKey="id"
                    size="small"
                    pagination={{ pageSize: 20 }}
                />
            </Modal>

            {/* Add Addresses Modal */}
            <Modal
                title={`Add Addresses to Pool: ${currentPool?.name || ''}`}
                open={addAddressModalVisible}
                onCancel={() => setAddAddressModalVisible(false)}
                onOk={() => addressForm.submit()}
            >
                <Form form={addressForm} layout="vertical" onFinish={handleAddAddresses}>
                    <Form.Item name="mode" label="Input Mode" initialValue="list">
                        <Select>
                            <Option value="list">Address List</Option>
                            <Option value="range">Address Range</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        noStyle
                        shouldUpdate={(prev, curr) => prev.mode !== curr.mode}
                    >
                        {({ getFieldValue }) =>
                            getFieldValue('mode') === 'list' ? (
                                <Form.Item
                                    name="addresses"
                                    label="每行一个：IP [MAC]"
                                    tooltip="MAC 留空 = 绑定时随机生成；Hetzner/OVH 单 IP 填机房分配的虚拟 MAC"
                                    rules={[{ required: true, message: '请输入地址' }]}
                                >
                                    <TextArea
                                        rows={8}
                                        placeholder={'203.0.113.10 00:50:56:00:12:34\n203.0.113.11 00:50:56:00:12:35\n203.0.113.12'}
                                        style={{ fontFamily: 'monospace' }}
                                    />
                                </Form.Item>
                            ) : (
                                <>
                                    <Form.Item
                                        name="start"
                                        label="Start Address"
                                        rules={[{ required: true, message: 'Please enter start address' }]}
                                    >
                                        <Input placeholder="e.g., 192.168.1.10" />
                                    </Form.Item>
                                    <Form.Item
                                        name="end"
                                        label="End Address"
                                        rules={[{ required: true, message: 'Please enter end address' }]}
                                    >
                                        <Input placeholder="e.g., 192.168.1.100" />
                                    </Form.Item>
                                </>
                            )
                        }
                    </Form.Item>
                </Form>
            </Modal>

            <Ipv6PrefixDrawer pool={v6Pool} onClose={() => { setV6Pool(null); loadPools(); }} />
            <RdnsPoolModal pool={rdnsPool} onClose={(changed) => { setRdnsPool(null); if (changed) loadPools(); }} />
            <RdnsRequestsDrawer open={rdnsOpen} onClose={() => setRdnsOpen(false)} />

            {/* Preallocate IPv6 Modal */}
            <Modal
                title={`Preallocate IPv6 - ${currentPool?.name || ''}`}
                open={preallocModalVisible}
                onCancel={() => setPreallocModalVisible(false)}
                onOk={() => preallocForm.submit()}
            >
                <Form form={preallocForm} layout="vertical" onFinish={handlePreallocate}>
                    <Form.Item
                        name="count"
                        label="Count"
                        rules={[{ required: true, message: 'Please enter count' }]}
                        extra="Number of IPv6 addresses to preallocate (1-100)"
                    >
                        <InputNumber style={{ width: '100%' }} min={1} max={100} />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default IpPoolManagement;
